import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { ClipboardList, ArrowLeft, Copy, Check, PhoneCall, ChevronDown, ChevronUp, RefreshCw, Share2, Lock, UploadCloud, ScanLine } from 'lucide-react';
import api from '../../utils/api';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import ProxyUploadModal from '../../components/ProxyUploadModal';
import ProxyTargetPicker from '../../components/ProxyTargetPicker';
import ProxyClaimPanel from '../../components/ProxyClaimPanel';
import { formatBJ, filenameTimestamp, calcExpiryFromRecords } from '../../utils/datetime';
import { copyToClipboard } from '../../utils/clipboard';
import { buildXlsx, shareFile } from '../../utils/share';

const HIST_KEY = 'dispatch_proxy_history'; // 本地批次索引（batchId 列表），跨刷新保留

function loadHist() {
  try { return JSON.parse(localStorage.getItem(HIST_KEY) || '[]'); } catch { return []; }
}
function saveHist(list) {
  try { localStorage.setItem(HIST_KEY, JSON.stringify(list.slice(0, 30))); } catch {}
}

// 排五 · 代提代传（管理员 + 代理商）：一个入口、页内切换（和证券的“代提代传”一致）
//   代提（派发数据）= 代对方领取数据，号码直接进对方的任务列表（原“代提数据”页）
//   代传（上传图片）= 代对方上传工作凭证图片，后台 OCR 识别，记录算在对方名下（原首页“上传图片”弹窗）
// 用 ?mode=upload 直接打开代传；缺省是代提（旧地址 /admin/proxy 的行为不变）。
export default function ProxyRequestPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const pageMode = params.get('mode') === 'upload' ? 'upload' : 'claim';
  const setPageMode = (m) => setParams(m === 'upload' ? { mode: 'upload' } : {}, { replace: true });
  const [uploadOpen, setUploadOpen] = useState(false);
  // 代提、代传各自记各自的对象，互不影响：常见情况是先给 A 代提数据，再给 B 代传图片（B 不一定是 A）。
  // 选好后选人卡片自动折叠；切换代提/代传时各自的选择都保留，代提提交成功后也不清空，方便连续给同一个人操作。
  const [claimTarget, setClaimTarget] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  // 代提成功弹窗：展示本次派发号码 + 一键复制
  const [result, setResult] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  // 代提批次历史：每批独立展示，确认弹窗关闭后仍可回来复制
  const [batches, setBatches] = useState([]); // 服务端批次明细
  const [historyIds, setHistoryIds] = useState([]); // 本地索引
  const [expandedId, setExpandedId] = useState(null); // 当前展开的批次
  const [histLoading, setHistLoading] = useState(false);
  const { showToast } = useToast();
  // 首次加载批次列表时自动展开最新一批（仅一次）
  const autoExpandedRef = useRef(false);

  const copyText = async (text, label = '已复制') => {
    await copyToClipboard(text);
    showToast(label, 'success');
  };

  const handleCopyAll = () => {
    const text = (result?.data || []).map(d => d.customer_phone || d.customer_name || '').filter(Boolean).join('\n');
    if (!text) return;
    copyText(text, `已复制全部 ${result.data.length} 条号码`);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 1500);
  };

  const handleCopyBatchAll = (b) => {
    const nums = (b.numbers || []).map(d => d.customer_phone || d.customer_name || '').filter(Boolean);
    if (!nums.length) return;
    copyText(nums.join('\n'), `已复制批次 ${nums.length} 个号码`);
  };

  // 转发代提结果为 xlsx 表格文档（Excel 直接打开，可转发微信/QQ；数据只有手机号）
  const handleShareResult = async () => {
    if (!result?.data?.length) return;
    // 弹窗刚开，数据不可能立即过期；但以防万一做兜底检查
    const expiryNote = calcExpiryFromRecords(result.data, 'imported_at');
    const label = result.targetName || (result.isExternal ? '外部接单人' : '用户');
    const res = await shareFile({
      filename: `${label}-${result.data.length}条-${filenameTimestamp()}.xlsx`,
      text: buildXlsx((result.data || []).map(d => ({ phone: d.customer_phone || '' })), expiryNote),
      ext: 'xlsx',
    });
    if (res === 'shared') showToast('已生成表格文档，请选择微信/QQ等发送', 'success');
    else if (res === 'download') showToast('当前浏览器不支持弹分享面板，已改为直接下载表格文件', 'info');
  };

  // 转发某个历史批次的 xlsx 文档（过期批次不允许转发）
  const handleShareBatch = async (b) => {
    if (b.isExpired) { showToast('数据已过期，无法转发', 'error'); return; }
    const items = b.numbers || [];
    if (!items.length) return;
    const label = b.user_name || b.user_username || (b.user_phone && !String(b.user_phone).startsWith('ext') ? b.user_phone : '') || `用户${b.user_id}`;
    const expiryNote = calcExpiryFromRecords(items, 'data_imported_at');
    const res = await shareFile({
      filename: `${label}-${items.length}条-${filenameTimestamp()}.xlsx`,
      text: buildXlsx(items.map(d => ({ phone: d.customer_phone || '' })), expiryNote),
      ext: 'xlsx',
    });
    if (res === 'shared') showToast('已生成表格文档，请选择微信/QQ等发送', 'success');
    else if (res === 'download') showToast('当前浏览器不支持弹分享面板，已改为直接下载表格文件', 'info');
  };

  // 复制某批次号码（过期批次不允许复制）
  const handleCopyBatchAllGuarded = (b) => {
    if (b.isExpired) { showToast('数据已过期，无法复制', 'error'); return; }
    handleCopyBatchAll(b);
  };

  const fetchBatches = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await api.get('/dispatch/proxy-batches');
      const list = res.data.batches || [];
      setBatches(list);
      // 默认展开最新一个批次，方便直接查看号码/转发（仅首次加载时）
      if (list.length > 0 && !autoExpandedRef.current) {
        autoExpandedRef.current = true;
        setExpandedId(list[0].batch_id);
      }
    } catch (err) { /* 历史加载失败不影响主流程 */ }
    finally { setHistLoading(false); }
  }, []);

  // 初次加载批次历史（本地索引 + 服务端明细）
  useEffect(() => {
    const ids = loadHist();
    setHistoryIds(ids);
    fetchBatches();
  }, [fetchBatches]);

  // 代提成功（ProxyClaimPanel 的回调）：记批次索引、刷新批次历史、弹出结果（号码 + 复制/转发）
  const handleDispatched = (data, targetUser) => {
    const newIds = [data.batchId, ...historyIds.filter(id => id !== data.batchId)];
    setHistoryIds(newIds);
    saveHist(newIds);
    fetchBatches();
    // 服务端在传了 userId 时返回的 isExternal 恒为 false，这里以选中的对象为准
    setResult({ ...data, isExternal: !!targetUser.is_external, targetName: targetUser.name });
    setCopiedAll(false);
  };

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {pageMode === 'upload' ? <UploadCloud size={16} /> : <ClipboardList size={16} />} {pageMode === 'upload' ? '代传图片' : '代提数据'}
        </span>
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>

        {/* ── 代提 / 代传 切换（和证券代提代传一样的分段控件）── */}
        <div style={{ display: 'flex', gap: 6, padding: 4, background: 'var(--c-bg)', borderRadius: 12, marginBottom: 12 }}>
          {[['claim', '代提（派发数据）', ClipboardList], ['upload', '代传（上传图片）', UploadCloud]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => setPageMode(key)} style={{
              flex: 1, height: 38, borderRadius: 10, border: 'none', cursor: 'pointer',
              background: pageMode === key ? 'var(--c-surface)' : 'transparent',
              color: pageMode === key ? 'var(--c-primary)' : 'var(--c-text-secondary)',
              fontSize: 14, fontWeight: pageMode === key ? 700 : 500, boxShadow: pageMode === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}><Icon size={15} /> {label}</button>
          ))}
        </div>

        {/* ── 选对象：代提、代传各选各的（key 不同 → 切换时各自保留自己的选择；选好后折叠成一行，点“更换”再展开）── */}
        {pageMode === 'claim'
          ? <ProxyTargetPicker key="claim" value={claimTarget} onChange={setClaimTarget} />
          : <ProxyTargetPicker key="upload" value={uploadTarget} onChange={setUploadTarget} />}

        {pageMode === 'upload' && (
          !uploadTarget ? (
            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--c-text-tertiary)', padding: 20 }}>先在上方选择代为上传的对象</p>
          ) : (
            <div className="card">
              <button className="btn btn-primary" onClick={() => setUploadOpen(true)} style={{ width: '100%', height: 48, fontSize: 15 }}>
                <UploadCloud size={17} /> 为 {uploadTarget.name} 代传图片
              </button>
              <button onClick={() => navigate('/admin/ocr')}
                style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--c-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <ScanLine size={14} /> 查看 OCR 识别记录
              </button>
              <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 8, lineHeight: 1.6, textAlign: 'center' }}>
                上传后后台自动 OCR 识别，记录和成功组数都算在【{uploadTarget.name}】名下；要删除 / 替换 / 补充图片，请到「图片管理」。
              </div>
            </div>
          )
        )}

        {pageMode === 'claim' && (<>
        {/* ── 代提：额度提示 + 接量数 + 提交（key 随对象变，换人时数量/额度一起重置）── */}
        {!claimTarget ? (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--c-text-tertiary)', padding: 20 }}>先在上方选择代为提取数据的对象</p>
        ) : (
          <ProxyClaimPanel key={claimTarget.id} targetUser={claimTarget} onDispatched={handleDispatched} />
        )}

        {/* ── 代提批次（每批独立展示，可随时回来复制） ── */}
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>代提批次</h3>
            <button className="btn btn-ghost btn-sm" onClick={fetchBatches} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <RefreshCw size={13} /> {histLoading ? '刷新中...' : '刷新'}
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 10, lineHeight: 1.6 }}>
            每提交一次即一个批次，可展开查看号码并复制；确认弹窗关闭或复制失败后，随时回到这里重新复制。
          </p>

          {batches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '18px 0', fontSize: 13, color: 'var(--c-text-tertiary)' }}>
              {histLoading ? '加载中...' : '暂无代提记录'}
            </div>
          ) : (
            batches.map((b, bi) => {
              // 服务端已按操作者过滤（submitted_by=本人），这里直接全部展示；
              // 不再用本地 historyIds 过滤（历史索引被清空/换设备时会漏显示整批）
              const nums = (b.numbers || []).map(d => d.customer_phone || d.customer_name || '').filter(Boolean);
              const isExpanded = expandedId === b.batch_id;
              const expired = !!b.isExpired;
              return (
                <div key={b.batch_id} style={{
                  border: `0.5px solid ${expired ? 'rgba(255,59,48,0.25)' : 'var(--c-border)'}`,
                  borderRadius: 10, marginBottom: 10, overflow: 'hidden',
                  opacity: expired ? 0.85 : 1,
                }}>
                  <div
                    onClick={() => !expired && setExpandedId(isExpanded ? null : b.batch_id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: expired ? 'not-allowed' : 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {b.is_external ? <span className="badge badge-warning" style={{ fontSize: 10 }}>外部</span> : null}
                        {expired && <Lock size={12} color="var(--c-danger)" style={{ flexShrink: 0 }} />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {b.user_name || b.user_username || (String(b.user_phone).startsWith('ext') ? '' : b.user_phone) || `用户${b.user_id}`}
                          {b.user_username && b.user_name !== b.user_username && <span style={{ color: 'var(--c-text-secondary)', fontWeight: 400 }}>（{b.user_username}）</span>}
                          {!b.user_username && b.user_phone && !String(b.user_phone).startsWith('ext') && `（${b.user_phone}）`}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: expired ? 'var(--c-danger)' : 'var(--c-text-secondary)', marginTop: 2 }}>
                        {(() => {
                          const full = formatBJ(b.created_at);
                          const short = full ? full.slice(5) : '';
                          return short;
                        })()} · {b.quantity_dispatched} 条
                        {expired
                          ? <span style={{ marginLeft: 6, fontWeight: 600 }}>已过期</span>
                          : b.expiryAt && <span style={{ marginLeft: 6, color: 'var(--c-accent)' }}>有效至 {b.expiryAt}</span>
                        }
                      </div>
                    </div>
                    {expired
                      ? <Lock size={16} color="var(--c-danger)" style={{ opacity: 0.5, flexShrink: 0 }} />
                      : (isExpanded ? <ChevronUp size={16} style={{ color: 'var(--c-text-secondary)' }} /> : <ChevronDown size={16} style={{ color: 'var(--c-text-secondary)' }} />)
                    }
                  </div>

                  {isExpanded && !expired && (
                    <div style={{ padding: '4px 12px 12px' }}>
                      <div style={{ maxHeight: 180, overflowY: 'auto', border: '0.5px solid var(--c-border)', borderRadius: 8, marginBottom: 8 }}>
                        {nums.length === 0 ? (
                          <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 12, color: 'var(--c-text-tertiary)' }}>暂无号码数据</div>
                        ) : nums.map((n, ni) => (
                          <div key={ni} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '8px 10px', borderBottom: ni < nums.length - 1 ? '0.5px solid var(--c-border)' : 'none'
                          }}>
                            <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 14, fontWeight: 600, color: 'var(--c-primary)', letterSpacing: '0.5px', wordBreak: 'break-all' }}>{n}</span>
                            <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '3px 8px', flexShrink: 0 }}
                              onClick={() => copyText(n, `已复制 ${n}`)}>
                              <Copy size={12} /> 复制
                            </button>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleCopyBatchAllGuarded(b)}
                          style={{
                            flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                            background: 'var(--c-primary-light)', border: '1px solid rgba(0,122,255,0.2)',
                            fontSize: 13, fontWeight: 600, color: 'var(--c-primary)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                          }}>
                          <PhoneCall size={14} /> 复制全部 {nums.length} 个
                        </button>
                        <button onClick={() => handleShareBatch(b)}
                          style={{
                            flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                            background: 'var(--c-success)', border: 'none',
                            fontSize: 13, fontWeight: 600, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                          }}>
                          <Share2 size={14} /> 转发文档
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ── 代提成功弹窗：展示本次派发号码，复制给用户 ── */}
        {result && (
          <div className="modal-overlay" onClick={() => setResult(null)}
            onTouchMove={e => e.preventDefault()}
            style={{ background: 'rgba(0,0,0,0.45)', position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', touchAction: 'none' }}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 'min(420px, 100%)', maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-handle" />
              <h3 style={{ fontSize: 17 }}>代提交成功</h3>
              <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', margin: '0 0 6px', lineHeight: 1.6 }}>
                已为{result.isExternal ? '外部接单人' : '用户'}
                {result.targetName ? `「${result.targetName}」` : ''}派发 <strong style={{ color: 'var(--c-primary)' }}>{result.dispatched}</strong> 条数据
                {result.partial ? <span style={{ color: 'var(--c-accent)' }}>（池中不足，实际派发 {result.dispatched} 条）</span> : null}：
              </p>
              {(() => {
                const expiry = calcExpiryFromRecords(result.data || [], 'imported_at');
                return expiry ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 10px', marginBottom: 10,
                    background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.3)',
                    borderRadius: 8, fontSize: 12, color: 'var(--c-accent)',
                  }}>
                    <span style={{ fontSize: 15 }}>⏰</span>
                    <span>数据有效期至：<strong>{expiry}</strong></span>
                  </div>
                ) : null;
              })()}

              <div style={{ overflowY: 'auto', flex: 1, border: '0.5px solid var(--c-border)', borderRadius: 10, marginBottom: 14 }}>
                {(result.data || []).map((d, i) => {
                  const phone = d.customer_phone || d.customer_name || '';
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 12px', borderBottom: i < (result.data || []).length - 1 ? '0.5px solid var(--c-border)' : 'none'
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', flex: 1, wordBreak: 'break-all', letterSpacing: '0.5px' }}>{phone}</span>
                      <button className="btn btn-outline btn-sm" style={{ flexShrink: 0, fontSize: 12, padding: '4px 10px' }}
                        onClick={() => copyText(phone, `已复制 ${phone}`)}>
                        <Copy size={13} /> 复制
                      </button>
                    </div>
                  );
                })}
                {(result.data || []).length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 13, color: 'var(--c-text-tertiary)' }}>没有可展示的数据</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-primary" onClick={handleCopyAll}
                  style={{ flex: 1, height: 44, fontSize: 15 }}>
                  {copiedAll ? <><Check size={16} /> 已复制全部</> : <><PhoneCall size={16} /> 一键复制全部 {result.dispatched} 条</>}
                </button>
                <button className="btn btn-primary" onClick={handleShareResult}
                  style={{ flex: 1, height: 44, fontSize: 15, background: 'var(--c-success)', borderColor: 'var(--c-success)' }}>
                  <Share2 size={16} /> 转发文档
                </button>
              </div>
              <button className="btn" onClick={() => setResult(null)}
                style={{ width: '100%', height: 42, fontSize: 14, marginTop: 8, background: 'none', border: '1px solid var(--c-border)', color: 'var(--c-text-secondary)' }}>
                完成
              </button>
            </div>
          </div>
        )}
        </>)}
      </div>

      {uploadOpen && uploadTarget && <ProxyUploadModal targetUser={uploadTarget} onClose={() => setUploadOpen(false)} />}
    </div>
  );
}